/**
 * Корень приложения: сессия решает, что показывать, роутер — какой экран.
 *
 * Пять экранов (scout-rewrite-spec.md §2): «Велик» (`/`, ввод номера),
 * карточка байка (`/bike/:id`), «Список» (`/list`), «Карта» (`/map`),
 * «Задания» (`/tasks`). `/scan` открывается кнопкой с главного экрана и
 * своей вкладки не имеет — как раньше `/task/:id`/`/vehicle/:id`.
 *
 * `createHashRouter`, а не `createBrowserRouter`: сборка отдаётся
 * Capacitor'ом с локального origin, где нет сервера, который вернул бы
 * index.html на произвольный путь. С обычным роутером обновление страницы
 * на /bike/<id> дало бы 404 — и это ровно тот момент, когда скаут
 * перезагружает приложение, потому что что-то пошло не так.
 *
 * ⚠ <Toaster> здесь НЕТ намеренно — он один на всё приложение и живёт в
 * providers.tsx. Тосты нужны и мимо роутера: отказ офлайн-очереди прилетает,
 * когда появилась сеть, независимо от того, какой экран открыт.
 */
import { Suspense, lazy } from 'react'
import { createHashRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom'
import LoginPage from '@/pages/login'
import HomePage from '@/pages/home'
import BikeCardPage from '@/pages/bike-card'
import ListPage from '@/pages/list'
import TasksPage from '@/pages/tasks'
import { TabBar } from '@/components/tabs'
import { useAuth } from '@/lib/auth'

/*
 * Карта и сканер — отдельными чанками. mapbox-gl — около 500 КБ gzip, и
 * первым каждый раз открывается главный экран; сканер тянет WASM-декодер
 * примерно на мегабайт СРАЗУ ПРИ ИМПОРТЕ, не при первом кадре. Затащить
 * любой из них в общий чанк значило бы платить этим весом на каждом
 * запуске, включая запуски ради того, чтобы просто ввести номер.
 */
const MapPage = lazy(() => import('@/pages/map'))
const ScanPage = lazy(() => import('@/pages/scan'))
/*
 * Режим «Сборщик»: у сборщика без скаутских ролей — всё приложение
 * (`collector-app.tsx`), у staff/admin — ветка `/collector/*` для проверки.
 * Оба лениво: скауту код сборщика не нужен.
 */
const CollectorApp = lazy(() => import('@/app/collector-app'))
const CollectorRoutes = lazy(() => import('@/pages/collector'))

/**
 * Режим сборщика внутри скаутского роутера — только тем, кого пустит сервер.
 * Скаут без права сборщика попадает сюда по оставшемуся hash (на общем
 * телефоне сборщик вышел, а `#/collector` остался) — и получил бы 403 на
 * каждом запросе.
 */
function CollectorGate() {
  const { canCollect } = useAuth()
  if (!canCollect) return <Navigate to="/" replace />
  return (
    <Suspense fallback={<p className="p-4 text-sm opacity-60">Загружаем…</p>}>
      <CollectorRoutes />
    </Suspense>
  )
}

/** Общая обёртка: панель разделов видна на всех экранах верхнего уровня. */
function Shell() {
  return (
    <>
      <Outlet />
      <TabBar />
    </>
  )
}

const router = createHashRouter([
  // Вне `Shell`: у режима сборщика свои вкладки, скаутские ему не нужны.
  { path: '/collector/*', element: <CollectorGate /> },
  {
    element: <Shell />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/bike/:id', element: <BikeCardPage /> },
      { path: '/list', element: <ListPage /> },
      { path: '/tasks', element: <TasksPage /> },
      {
        path: '/map',
        element: (
          <Suspense fallback={<p className="p-4 text-sm opacity-60">Загружаем карту…</p>}>
            <MapPage />
          </Suspense>
        ),
      },
      {
        path: '/scan',
        element: (
          <Suspense fallback={<p className="p-4 text-sm opacity-60">Загружаем сканер…</p>}>
            <ScanPage />
          </Suspense>
        ),
      },
      // Любой другой путь — на главный экран. Скаут не должен упереться в
      // пустой экран.
      { path: '*', element: <HomePage /> },
    ],
  },
])

export function App() {
  const { userId, loading, roles, isScout, isPartner, isCollectorOnly } = useAuth()

  if (loading) {
    return (
      <main className="flex min-h-dvh items-center justify-center">
        <p className="opacity-60">Загружаем…</p>
      </main>
    )
  }

  if (!userId) return <LoginPage />

  // Сборщику `/v1/scout/*` отвечает 403 — скаутских экранов он не видит вовсе.
  if (isCollectorOnly) {
    return (
      <Suspense fallback={<p className="p-4 text-sm opacity-60">Загружаем…</p>}>
        <CollectorApp />
      </Suspense>
    )
  }

  return (
    <>
      {!isScout && !isPartner ? (
        // Предупреждение, а не отказ: под staff проходит всё, и разбор
        // инцидентов ведётся из-под него.
        <p className="bg-amber-500/15 px-4 py-2 text-center text-sm text-amber-200">
          У вас нет роли scout или partner_scout ({roles.join(', ') || 'ролей нет'}). Часть
          запросов вернёт 403.
        </p>
      ) : null}
      <RouterProvider router={router} />
    </>
  )
}
